import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp, Coins, Trophy, ArrowRight, Sparkles, CircleCheck, Hourglass,
  AlertTriangle, MessageSquare,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { timeAgo, statusLabel, canCreateRequest, isOwner, isDeptAdmin } from "@/lib/utils";

function jscoreTier(total) {
  if (total < 25) {
    return {
      label: "Dormant", message: "Activity is critically low.",
      text: "text-red-700", ring: "#ef4444", chip: "bg-red-100 text-red-700",
      bg: "from-red-50 via-white to-white", border: "border-red-200",
    };
  }
  if (total < 50) {
    return {
      label: "Activating", message: "Picking up, still fragile.",
      text: "text-amber-700", ring: "#f59e0b", chip: "bg-amber-100 text-amber-700",
      bg: "from-amber-50 via-white to-white", border: "border-amber-200",
    };
  }
  if (total < 75) {
    return {
      label: "Healthy", message: "Active and engaged.",
      text: "text-blue-700", ring: "#2563eb", chip: "bg-blue-100 text-blue-700",
      bg: "from-blue-50 via-white to-white", border: "border-blue-200",
    };
  }
  return {
    label: "Thriving", message: "Highly active org.",
    text: "text-emerald-700", ring: "#10b981", chip: "bg-emerald-100 text-emerald-700",
    bg: "from-emerald-50 via-white to-white", border: "border-emerald-200",
  };
}

export default function Dashboard() {
  const { user, org } = useAuth();
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [budget, setBudget] = useState(null);
  const [pending, setPending] = useState([]);
  const [jscore, setJscore] = useState(null);
  const [scope] = useState(
    isDeptAdmin(user) ? "department" : "org"
  );

  const load = async () => {
    // Department admins are always scoped to their department — no org-wide view.
    const effectiveScope = isDeptAdmin(user) ? "department" : scope;
    const params = effectiveScope === "department"
      ? { scope: "department", department: user?.department }
      : { scope: "org" };
    const [s, req] = await Promise.all([
      api.get("/dashboard/stats", { params }),
      api.get("/requests"),
    ]);
    setStats(s.data);
    setRequests(req.data.slice(0, 8));
    // Owners have no home department — skip personal budget card.
    if (user?.department && !isOwner(user)) {
      api.get("/department-budgets/me").then((r) => setBudget(r.data)).catch(() => setBudget(null));
    } else {
      setBudget(null);
    }
    if (isDeptAdmin(user)) {
      api.get("/requests", { params: { status: "pending_approval" } })
        .then((r) => setPending(r.data.slice(0, 5)))
        .catch(() => setPending([]));
    }
    if (isOwner(user)) {
      api.get("/dashboard/jscore").then((r) => setJscore(r.data)).catch(() => setJscore(null));
    }
  };

  useEffect(() => { load(); }, [scope, user?.department, user?.role]);

  if (!stats) return <div className="text-slate-500 font-mono text-sm">Loading…</div>;

  const budgetPct = budget && budget.allocated_credits > 0
    ? Math.min(100, Math.round((budget.spent_credits / budget.allocated_credits) * 100))
    : 0;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4 rise">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-blue-600">Welcome back</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-2">
            Hello, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-slate-600 mt-2">
            {stats.open_requests} open requests
            {isDeptAdmin(user) || stats.scope === "department"
              ? ` in ${stats.department || user?.department || "your department"}`
              : ` in ${org?.name}`}.
            {!isOwner(user) && (
              <> Your balance is <span className="font-mono font-semibold">{stats.my_balance}</span> credits.</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canCreateRequest(user) && (
            <Button asChild data-testid="dashboard-create-btn" className="bg-blue-600 hover:bg-blue-700">
              <Link to="/app/requests/new">Post a request <ArrowRight className="h-4 w-4 ml-1" /></Link>
            </Button>
          )}
        </div>
      </div>

      <div className={`grid grid-cols-2 ${isOwner(user) ? "lg:grid-cols-6" : "lg:grid-cols-4"} gap-4 rise rise-1`}>
        {isOwner(user) ? (
          <>
            {jscore && <div className="col-span-2 h-full"><JScoreKpi jscore={jscore} /></div>}
            <Kpi icon={Hourglass} color="from-blue-600 to-blue-700" label="Open requests" value={stats.open_requests} sub="Across the org" testid="kpi-open" />
            <Kpi icon={AlertTriangle} color="from-orange-500 to-orange-600" label="Pending approval" value={stats.pending_approval || 0} sub="Over-max bounty waits" testid="kpi-pending-approval" />
            <Kpi icon={Coins} color="from-emerald-500 to-emerald-600" label="Credits awarded" value={stats.credits_awarded} sub="Paid to solvers" testid="kpi-awarded" />
            <Kpi icon={Trophy} color="from-slate-700 to-slate-900" label="Active contributors" value={stats.active_contributors} sub={`${stats.participation_rate}% participation`} testid="kpi-active" />
          </>
        ) : (
          <>
            <Kpi icon={Coins} color="from-blue-600 to-blue-700" label="Credit balance" value={stats.my_balance} sub={`${stats.my_credits_earned} earned`} testid="kpi-balance" />
            <Kpi icon={Trophy} color="from-amber-500 to-amber-600" label="Reputation" value={stats.my_reputation} sub="Across all activity" testid="kpi-reputation" />
            <Kpi icon={CircleCheck} color="from-emerald-500 to-emerald-600" label="You solved" value={stats.my_solved} sub="Approved solutions" testid="kpi-solved" />
            <Kpi icon={Hourglass} color="from-slate-700 to-slate-900" label="You posted" value={stats.my_open} sub="Total requests created" testid="kpi-posted" />
          </>
        )}
      </div>

      {(budget || (isDeptAdmin(user) && pending.length > 0) || stats.pending_review > 0) && (
        <div className="grid md:grid-cols-3 gap-4 rise rise-1">
          {budget && (
            <Card className="border-slate-200" data-testid="budget-health">
              <CardContent className="p-5">
                <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Department budget</div>
                <div className="font-display text-xl font-semibold mt-1">{budget.department}</div>
                <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-blue-600 rounded-full" style={{ width: `${budgetPct}%` }} />
                </div>
                <div className="text-xs font-mono text-slate-500 mt-2">
                  {budget.remaining_credits} remaining · max {budget.max_credits_per_ticket}c / ticket
                </div>
              </CardContent>
            </Card>
          )}
          {isDeptAdmin(user) && (
            <Card className="border-orange-200 bg-orange-50/40" data-testid="needs-approval-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-orange-800">
                  <AlertTriangle className="h-4 w-4" />
                  <div className="text-xs font-mono uppercase tracking-widest">Needs approval</div>
                </div>
                <div className="font-display text-3xl font-bold mt-2">{stats.my_pending_approvals || pending.length}</div>
                <Link to="/app/admin" className="text-xs text-blue-600 hover:underline mt-2 inline-block">
                  Review in Owner panel →
                </Link>
                {pending.slice(0, 2).map((r) => (
                  <Link key={r.id} to={`/app/requests/${r.id}`} className="block text-sm mt-2 truncate hover:text-blue-600">
                    {r.title}
                  </Link>
                ))}
              </CardContent>
            </Card>
          )}
          <Card className="border-slate-200" data-testid="pending-review-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-slate-600">
                <MessageSquare className="h-4 w-4" />
                <div className="text-xs font-mono uppercase tracking-widest">Awaiting your review</div>
              </div>
              <div className="font-display text-3xl font-bold mt-2">{stats.pending_review || 0}</div>
              <Link to="/app/board" className="text-xs text-blue-600 hover:underline mt-2 inline-block">
                Open board →
              </Link>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6 rise rise-2">
        <Card className="lg:col-span-2 border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-slate-500">
                  {stats.scope === "department" ? `${stats.department} · 14d` : "Org activity · 14d"}
                </div>
                <h3 className="font-display text-2xl font-semibold tracking-tight mt-1">Requests created vs solved</h3>
              </div>
              <div className="flex gap-3 text-xs">
                <Legend dot="#2563eb" label="Created" />
                <Legend dot="#10b981" label="Solved" />
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.activity}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tickLine={false} axisLine={false} fontSize={11} stroke="#94a3b8" />
                  <YAxis tickLine={false} axisLine={false} fontSize={11} stroke="#94a3b8" />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
                  <Line type="monotone" dataKey="created" stroke="#2563eb" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="solved" stroke="#10b981" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Pulse</div>
            <h3 className="font-display text-2xl font-semibold tracking-tight mt-1">{stats.total_requests} requests</h3>
            <div className="mt-6 space-y-4">
              <Row label="Open" value={stats.open_requests} accent="bg-blue-500" />
              <Row label="In motion" value={stats.in_progress} accent="bg-violet-500" />
              <Row label="Pending approval" value={stats.pending_approval || 0} accent="bg-orange-500" />
              <Row label="Completed" value={stats.completed} accent="bg-emerald-500" />
              <Row label="Credits awarded" value={stats.credits_awarded} accent="bg-amber-500" />
              <div className="pt-3 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Participation</span>
                  <span className="font-mono text-sm font-semibold">{stats.participation_rate}%</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">{stats.active_contributors} of {stats.total_users} active in last 30 days</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 rise rise-3">
        <Card className="lg:col-span-1 border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-4 w-4 text-blue-600" />
              <h3 className="font-display text-lg font-semibold tracking-tight">Top categories</h3>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={(stats.categories || []).slice(0, 6)} layout="vertical">
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" tickLine={false} axisLine={false} fontSize={11} width={120} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="count" fill="#2563eb" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold tracking-tight">Latest requests</h3>
              <Link to="/app/board" className="text-xs text-blue-600 hover:underline" data-testid="dashboard-view-all">View all →</Link>
            </div>
            <div className="divide-y divide-slate-200">
              {requests.map((r) => (
                <Link
                  key={r.id}
                  to={`/app/requests/${r.id}`}
                  data-testid={`dashboard-request-${r.id}`}
                  className="flex items-center gap-4 py-3 group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate group-hover:text-blue-600 transition-colors">{r.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                      <span>{r.category}</span> · <span>{timeAgo(r.created_at)}</span>
                    </div>
                  </div>
                  <Badge variant="outline" className="font-mono">{r.bounty_credits}c</Badge>
                  <span className={`pill pill-${r.status}`}>{statusLabel(r.status)}</span>
                </Link>
              ))}
              {requests.length === 0 && (
                <div className="text-center py-8 text-slate-500 text-sm">No requests yet. Create your first one!</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const JScoreKpi = ({ jscore }) => {
  const tier = jscoreTier(jscore.total);
  const breakdown = [
    { label: "Participation", value: jscore.p, max: 30 },
    { label: "Solve rate", value: jscore.s, max: 25 },
    { label: "Credit velocity", value: jscore.c, max: 25 },
    { label: "Consistency", value: jscore.a, max: 20 },
  ];
  return (
    <Card className={`card-hover overflow-hidden border ${tier.border} bg-gradient-to-br ${tier.bg} p-0 h-full flex flex-col`} data-testid="kpi-jscore">
      <CardContent className="p-4 flex-1">
        <div className="flex items-center gap-4">
          <div className="relative h-11 w-11 shrink-0 drop-shadow-sm">
            <PieChart width={44} height={44}>
              <Pie
                data={[{ value: jscore.total }, { value: 100 - jscore.total }]}
                dataKey="value"
                innerRadius={15}
                outerRadius={21}
                startAngle={90}
                endAngle={-270}
                stroke="none"
                cornerRadius={4}
              >
                <Cell fill={tier.ring} />
                <Cell fill="#0f172a" fillOpacity={0.08} />
              </Pie>
            </PieChart>
            <div className="absolute inset-0 grid place-items-center">
              <TrendingUp className="h-3.5 w-3.5 opacity-0" />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display text-2xl font-bold tracking-tight">{jscore.total}</span>
              <span className="text-xs text-slate-400 font-mono -ml-1">/100</span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${tier.chip}`}>{tier.label}</span>
            </div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mt-0.5">J-Score</div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2 mt-3">
          {breakdown.map((b) => (
            <div key={b.label} className="min-w-0">
              <div className="text-[9px] text-slate-500 truncate">{b.label}</div>
              <div className="text-[11px] font-mono font-semibold text-slate-700">{b.value}/{b.max}</div>
            </div>
          ))}
        </div>
      </CardContent>
      <div
        className="px-4 py-1.5 text-xs font-semibold text-white text-center tracking-wide truncate"
        style={{ background: `linear-gradient(90deg, ${tier.ring}, ${tier.ring}cc)` }}
      >
        {tier.label} — {tier.message}
      </div>
    </Card>
  );
};

const Kpi = ({ icon: Icon, color, label, value, sub, testid }) => (
  <Card className="border-slate-200 card-hover h-full" data-testid={testid}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${color} grid place-items-center text-white shadow`}>
          <Icon className="h-5 w-5" />
        </div>
        <TrendingUp className="h-4 w-4 text-slate-300" />
      </div>
      <div className="mt-4 font-display text-3xl font-bold tracking-tight">{value}</div>
      <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{label}</div>
      <div className="text-xs text-slate-400 mt-1">{sub}</div>
    </CardContent>
  </Card>
);

const Row = ({ label, value, accent }) => (
  <div className="flex items-center gap-3">
    <span className={`h-2 w-2 rounded-full ${accent}`} />
    <span className="text-sm flex-1">{label}</span>
    <span className="font-mono font-semibold">{value}</span>
  </div>
);

const Legend = ({ dot, label }) => (
  <span className="flex items-center gap-1.5 text-slate-600">
    <span className="h-2 w-2 rounded-full" style={{ background: dot }} /> {label}
  </span>
);
